/**
 * DesignLab.jsx — Admin-only Design Sandbox
 * Route: /admin/design-lab
 *
 * STRICT RULES:
 * - NO backend API calls  - NO live data reads  - NO database writes
 * - NO modifications to the production guest site  - All data is MOCK only
 */

import React, { useState } from 'react';
import { Utensils, X } from 'lucide-react';

// ─── Mock Data ─────────────────────────────────────────────────────────────────
const MOCK_PARTY = {
  party_name: 'Stark Family',
  guests: [
    { id: 'g1', first_name: 'Amor',  last_name: 'Stark' },
    { id: 'g2', first_name: 'Howie', last_name: 'Stark' },
    { id: 'g3', first_name: 'Toni',  last_name: 'Stark' },
  ],
};
const MOCK_MENU = {
  'Appetizer': [
    'Caprese with Fresh Burrata & Micro Basil',
    'Shrimp Cocktail with Spiced Cocktail Sauce',
    'Bruschetta al Pomodoro e Basilico',
  ],
  'Main Course': [
    'Chilean Sea Bass with Citrus Beurre Blanc',
    'Filet Mignon with Truffle Demi-Glace',
    'Chicken Piccata with Lemon Caper Sauce',
  ],
  'Dessert': [
    'Tiramisu Classico',
    'Crème Brûlée with Caramelized Sugar',
    'Mixed Berry Panna Cotta',
  ],
};

const RSVP_STATES = [
  { key: 'pending',   label: 'Pending',            color: '#9B7420' },
  { key: 'accepting', label: 'Joyfully Accepting', color: '#2D7A4F' },
  { key: 'declining', label: 'Regretfully No',     color: '#9B3030' },
  { key: 'submitted', label: 'Already Submitted',  color: '#4F46E5' },
];

const DIETARY_OPTIONS = [
  { key: 'veg',   label: 'Vegetarian', icon: '🥗' },
  { key: 'gf',    label: 'Gluten-Free', icon: '🌾' },
  { key: 'vegan', label: 'Vegan',       icon: '🌿' },
  { key: 'df',    label: 'Dairy-Free',  icon: '🥛' },
  { key: 'nut',   label: 'Nut Allergy', icon: '🥜' },
  { key: 'fish',  label: 'No Seafood',  icon: '🐟' },
  { key: 'diab',  label: 'Diabetic',    icon: '⚕' },
];

// ─── Adult / Child SVG Icons ──────────────────────────────────────────────────
// Restroom-style silhouettes — adult is taller, child is smaller (same design)
const AdultIcon = ({ active }) => {
  const fill = active ? '#B8902A' : 'rgba(198,167,90,0.38)';
  return (
    <svg width="14" height="26" viewBox="0 0 14 26" fill="none"
      style={{ transition:'all 0.16s ease', transform: active ? 'scale(1.10)' : 'scale(1)' }}>
      {/* Head */}
      <circle cx="7" cy="4" r="3.5" fill={fill} />
      {/* Body */}
      <rect x="3" y="9" width="8" height="10" rx="2" fill={fill} />
      {/* Left leg */}
      <rect x="3" y="19" width="3.2" height="7" rx="1.4" fill={fill} />
      {/* Right leg */}
      <rect x="7.8" y="19" width="3.2" height="7" rx="1.4" fill={fill} />
    </svg>
  );
};

const ChildIcon = ({ active }) => {
  const fill = active ? '#B8902A' : 'rgba(198,167,90,0.38)';
  return (
    <svg width="11" height="21" viewBox="0 0 11 21" fill="none"
      style={{ transition:'all 0.16s ease', transform: active ? 'scale(1.10)' : 'scale(1)' }}>
      {/* Head — slightly larger ratio (child proportion) */}
      <circle cx="5.5" cy="3.5" r="3.2" fill={fill} />
      {/* Body */}
      <rect x="2" y="8" width="7" height="8" rx="1.8" fill={fill} />
      {/* Left leg */}
      <rect x="2" y="16" width="2.7" height="5" rx="1.2" fill={fill} />
      {/* Right leg */}
      <rect x="6.3" y="16" width="2.7" height="5" rx="1.2" fill={fill} />
    </svg>
  );
};

// ─── Adult / Child Pill Selector ───────────────────────────────────────────────
function AdultChildSelector({ value, onChange, hasError }) {
  return (
    <div style={{
      display:'flex', borderRadius:999, overflow:'hidden',
      border: hasError
        ? '1.5px solid rgba(155,48,48,0.55)'
        : '1.5px solid rgba(198,167,90,0.40)',
      boxShadow: hasError
        ? '0 0 10px rgba(155,48,48,0.14), inset 0 1px 0 rgba(255,255,255,0.60)'
        : 'inset 0 1px 0 rgba(255,255,255,0.68), 0 1px 4px rgba(110,88,30,0.06)',
      background:'rgba(255,253,246,0.70)',
      transition:'all 0.15s ease',
    }}>
      {/* Adult side */}
      <button onClick={() => onChange('adult')} style={{
        display:'flex', flexDirection:'column', alignItems:'center', gap:3,
        padding:'7px 11px 6px',
        background: value === 'adult'
          ? 'linear-gradient(160deg, rgba(198,167,90,0.20), rgba(198,167,90,0.10))'
          : 'transparent',
        borderRight:'1px solid rgba(198,167,90,0.28)',
        cursor:'pointer', border:'none', borderRight:'1px solid rgba(198,167,90,0.28)',
        boxShadow: value === 'adult' ? '0 0 10px rgba(198,167,90,0.18)' : 'none',
        transition:'all 0.16s ease',
        outline:'none',
      }}>
        <AdultIcon active={value === 'adult'} />
        <span style={{ fontSize:7, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px',
          color: value === 'adult' ? '#B8902A' : 'rgba(198,167,90,0.45)' }}>
          Adult
        </span>
      </button>

      {/* Child side */}
      <button onClick={() => onChange('child')} style={{
        display:'flex', flexDirection:'column', alignItems:'center', gap:3,
        padding:'7px 11px 6px',
        background: value === 'child'
          ? 'linear-gradient(160deg, rgba(198,167,90,0.20), rgba(198,167,90,0.10))'
          : 'transparent',
        cursor:'pointer', border:'none',
        boxShadow: value === 'child' ? '0 0 10px rgba(198,167,90,0.18)' : 'none',
        transition:'all 0.16s ease',
        outline:'none',
      }}>
        <ChildIcon active={value === 'child'} />
        <span style={{ fontSize:7, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px',
          color: value === 'child' ? '#B8902A' : 'rgba(198,167,90,0.45)' }}>
          Child
        </span>
      </button>
    </div>
  );
}

// ─── Gold Checkbox ─────────────────────────────────────────────────────────────
function GoldCheckbox({ checked, onChange }) {
  return (
    <div onClick={onChange} style={{
      width:18, height:18, borderRadius:5, flexShrink:0,
      border: checked ? '1.5px solid rgba(198,167,90,0.80)' : '1.5px solid rgba(198,167,90,0.30)',
      background: checked ? 'rgba(198,167,90,0.14)' : 'rgba(255,253,246,0.80)',
      display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
      boxShadow: checked
        ? '0 0 8px rgba(198,167,90,0.28), inset 0 1px 0 rgba(255,255,255,0.55)'
        : 'inset 0 1px 0 rgba(255,255,255,0.70)',
      transition:'all 0.15s ease',
    }}>
      {checked && <span style={{ color:'#B8902A', fontSize:12, fontWeight:700, lineHeight:1 }}>✓</span>}
    </div>
  );
}

// ─── Art Deco Botanical ────────────────────────────────────────────────────────
const Botanical = () => (
  <svg width="56" height="200" viewBox="0 0 56 200" fill="none"
    style={{ position:'absolute', right:0, top:'6%', opacity:0.17, pointerEvents:'none' }}>
    <path d="M44 200 C43 152 41 108 39 70 C37 44 34 20 30 5" stroke="#C6A75A" strokeWidth="0.9" fill="none"/>
    <path d="M40 175 C29 162 17 155 4 159" stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M39 142 C27 128 15 122 3 125" stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M38 108 C27 94 16 88 6 91"   stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M37 74  C27 60 18 54 10 57"  stroke="#C6A75A" strokeWidth="0.7" fill="none"/>
    <path d="M41 155 C49 142 53 129 54 117" stroke="#C6A75A" strokeWidth="0.6" fill="none"/>
    <path d="M40 118 C48 105 52 92 53 80"  stroke="#C6A75A" strokeWidth="0.6" fill="none"/>
    <ellipse cx="30" cy="5" rx="3" ry="5.5" stroke="#C6A75A" strokeWidth="0.7" transform="rotate(-12 30 5)" fill="none"/>
    <ellipse cx="30" cy="5" rx="3" ry="5.5" stroke="#C6A75A" strokeWidth="0.7" transform="rotate(12 30 5)" fill="none"/>
    <circle cx="30" cy="3" r="1.8" stroke="#C6A75A" strokeWidth="0.6" fill="none"/>
  </svg>
);

// ─── Per-guest row ─────────────────────────────────────────────────────────────
function GuestRow({ guest, index, guestType, onGuestTypeChange, showValidation }) {
  const [showDietary, setShowDietary] = useState(false);
  const [showSpecial,  setShowSpecial]  = useState(false);
  const [dietarySel,  setDietarySel]  = useState([]);
  const toggle = k => setDietarySel(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const hasTypeError = showValidation && !guestType;

  return (
    <div style={{
      borderBottom: '1px solid rgba(175,148,230,0.18)',
      borderLeft:   '3px solid rgba(175,148,230,0.36)',
      paddingLeft:12, paddingBottom:14, marginBottom:14,
    }}>
      {/* Name row + right column */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>

        {/* Left: label + etched name */}
        <div style={{ paddingTop:1 }}>
          <p style={{ fontSize:8, fontWeight:700, textTransform:'uppercase',
            letterSpacing:'1.2px', color:'#8A7AB0', margin:'0 0 5px' }}>
            Guest {index + 1}
          </p>
          <p style={{
            fontFamily:"'Cinzel',serif", fontSize:15, fontWeight:600, margin:0,
            letterSpacing:'0.3px', color:'#1A1410',
            textShadow:[
              '0 1px 1px rgba(255,255,255,0.88)',
              '0 -1px 0 rgba(24,16,4,0.18)',
              '0 2px 4px rgba(24,16,4,0.10)',
            ].join(', '),
          }}>
            {guest.first_name} {guest.last_name}
          </p>
        </div>

        {/* Right column: selector → dietary checkbox → special checkbox */}
        <div style={{ display:'flex', flexDirection:'column', gap:8, flexShrink:0, alignItems:'flex-end' }}>
          {/* 1. Adult / Child selector */}
          <AdultChildSelector
            value={guestType}
            onChange={onGuestTypeChange}
            hasError={hasTypeError}
          />

          {/* 2. Dietary Restrictions */}
          <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }}>
            <GoldCheckbox checked={showDietary} onChange={() => setShowDietary(v => !v)} />
            <span style={{ fontSize:11, fontWeight:500, color:'#5A4E38', whiteSpace:'nowrap' }}>
              Dietary Restrictions
            </span>
          </label>

          {/* 3. Special Request */}
          <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }}>
            <GoldCheckbox checked={showSpecial} onChange={() => setShowSpecial(v => !v)} />
            <span style={{ fontSize:11, fontWeight:500, color:'#5A4E38', whiteSpace:'nowrap' }}>
              Special Request
            </span>
          </label>
        </div>
      </div>

      {/* Validation error for no type selection */}
      {hasTypeError && (
        <p style={{ fontSize:10, color:'#9B3030', margin:'6px 0 0',
          fontStyle:'italic' }}>
          Please select Adult or Child for each guest.
        </p>
      )}

      {/* Dietary expansion — accordion, pushes content down */}
      <div style={{
        overflow:'hidden',
        maxHeight: showDietary ? '220px' : '0px',
        transition:'max-height 0.30s ease',
      }}>
        <div style={{ paddingTop:10, overflowX:'auto', scrollbarWidth:'none' }}>
          <div style={{ display:'flex', gap:5, minWidth:'max-content', paddingBottom:2 }}>
            {DIETARY_OPTIONS.map(d => (
              <button key={d.key} onClick={() => toggle(d.key)} style={{
                display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                padding:'7px 8px', borderRadius:10, cursor:'pointer',
                background: dietarySel.includes(d.key) ? 'rgba(198,167,90,0.14)' : 'rgba(255,253,246,0.82)',
                border: dietarySel.includes(d.key)
                  ? '1.5px solid rgba(198,167,90,0.55)'
                  : '1px solid rgba(198,167,90,0.22)',
                boxShadow: dietarySel.includes(d.key)
                  ? '0 0 8px rgba(198,167,90,0.18), inset 0 1px 0 rgba(255,255,255,0.65)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.65)',
                transition:'all 0.14s ease', outline:'none',
              }}>
                <span style={{ fontSize:14 }}>{d.icon}</span>
                <span style={{ fontSize:8, fontWeight:600, color:'#7A6A50',
                  letterSpacing:'0.2px', whiteSpace:'nowrap' }}>{d.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Special request expansion */}
      <div style={{
        overflow:'hidden',
        maxHeight: showSpecial ? '120px' : '0px',
        transition:'max-height 0.30s ease',
      }}>
        <div style={{ paddingTop:10 }}>
          <textarea
            placeholder="Need wheelchair access, etc."
            rows={2}
            style={{
              width:'100%', padding:'10px 12px', boxSizing:'border-box',
              borderRadius:10, resize:'none',
              border:'1.5px solid rgba(198,167,90,0.26)',
              background:'rgba(255,253,246,0.72)',
              boxShadow:'inset 0 1px 3px rgba(140,110,30,0.04), inset 0 1px 0 rgba(255,255,255,0.65)',
              fontSize:12, color:'#2A2218', fontFamily:'inherit', outline:'none', lineHeight:1.5,
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Menu Tiles — provided code, pasted exactly ────────────────────────────────
function MenuTiles({ activeMenu, setActiveMenu, menuData }) {
  const categories = ['Appetizer', 'Main Course', 'Dessert'];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {categories.map((cat) => {
          const isActive = activeMenu === cat;
          return (
            <button key={cat} onClick={() => setActiveMenu(isActive ? null : cat)}
              className={`relative min-h-[110px] rounded-[1.5rem] px-3 py-4 flex flex-col items-center justify-center transition-all duration-700 border backdrop-blur-xl overflow-hidden ${
                isActive
                  ? 'bg-white/85 border-[#D4AF37]/70 shadow-[0_20px_40px_rgba(212,175,55,0.12)] -translate-y-2'
                  : 'bg-white/30 border-[#D4AF37]/25 hover:bg-white/50 hover:border-[#D4AF37]/45'
              }`}>
              <div className="absolute inset-0 opacity-[0.08] pointer-events-none bg-[radial-gradient(circle_at_top,#D4AF37,transparent_55%)]" />
              <Utensils className={`w-7 h-7 mb-2 transition-all duration-500 ${
                isActive ? 'text-[#C9A227] scale-110' : 'text-[#D4AF37]/45'
              }`} />
              <span className="text-[0.72rem] uppercase text-center leading-tight"
                style={{ fontFamily:'Cinzel, serif', letterSpacing:'0.14em', color:'#1E1A17', fontWeight:600 }}>
                {cat}
              </span>
              {isActive && (
                <div className="mt-2 flex items-center gap-2 opacity-80">
                  <div className="w-5 h-px bg-[#D4AF37]/40" />
                  <div className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                  <div className="w-5 h-px bg-[#D4AF37]/40" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      {activeMenu && (
        <div className="animate-in slide-in-from-bottom-8 fade-in duration-700">
          <div className="bg-white/78 backdrop-blur-2xl border border-[#D4AF37]/35 rounded-[1.8rem] shadow-[0_24px_50px_rgba(0,0,0,0.12)] px-6 py-7 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-[0.08] bg-[radial-gradient(circle_at_top,#D4AF37,transparent_45%)]" />
            <button onClick={() => setActiveMenu(null)}
              className="absolute top-4 right-5 text-[#C19A35] hover:scale-110 transition-transform"
              aria-label="Close menu">
              <X className="w-5 h-5" />
            </button>
            <div className="relative text-center mb-7">
              <p className="text-[10px] uppercase mb-3"
                style={{ fontFamily:'Inter, sans-serif', letterSpacing:'0.55em', color:'#B8962E', fontWeight:700 }}>
                Menu Selection
              </p>
              <h4 className="text-[2.1rem] leading-none text-[#1E1A17]"
                style={{ fontFamily:'Cormorant Garamond, serif' }}>
                {activeMenu === 'Appetizer' ? 'Appetizers'
                  : activeMenu === 'Main Course' ? 'Main Course' : 'Desserts'}
              </h4>
              <div className="flex justify-center items-center gap-3 mt-4">
                <div className="w-10 h-px bg-[#D4AF37]/35" />
                <div className="text-[#D4AF37] text-[9px]">✦</div>
                <div className="w-10 h-px bg-[#D4AF37]/35" />
              </div>
            </div>
            <ul className="relative max-w-md mx-auto space-y-5">
              {menuData[activeMenu].map((item, i) => (
                <li key={i} className="text-center">
                  <p className="text-[1.6rem] leading-snug text-[#2B241D]"
                    style={{ fontFamily:'Cormorant Garamond, serif' }}>{item}</p>
                  {i < menuData[activeMenu].length - 1 && (
                    <div className="mt-4 flex justify-center">
                      <div className="flex items-center gap-3 opacity-40">
                        <div className="w-8 h-px bg-[#D4AF37]" />
                        <div className="text-[#D4AF37] text-[8px]">✦</div>
                        <div className="w-8 h-px bg-[#D4AF37]" />
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── RSVP Card ─────────────────────────────────────────────────────────────────
function RSVPCard({ rsvpState }) {
  const isPending   = rsvpState === 'pending';
  const isAccepting = rsvpState === 'accepting';
  const isDeclining = rsvpState === 'declining';
  const isSubmitted = rsvpState === 'submitted';

  // Guest type state lifted to RSVPCard for validation
  const [guestTypes, setGuestTypes] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  const handleGuestTypeChange = (guestId, type) => {
    setGuestTypes(prev => ({ ...prev, [guestId]: type }));
    if (showValidation) setShowValidation(false);
  };

  const handleSubmit = () => {
    const allSelected = MOCK_PARTY.guests.every(g => guestTypes[g.id]);
    if (!allSelected) {
      setShowValidation(true);
      return;
    }
    // sandbox — no real submit
    alert('RSVP submitted (sandbox demo)');
  };

  const goldEtched = {
    color:'#B8902A',
    textShadow:[
      '0 1.5px 0 rgba(255,255,255,0.93)',
      '0 -1px 0 rgba(80,50,0,0.12)',
      '0 2px 5px rgba(80,50,0,0.07)',
    ].join(', '),
  };

  return (
    <div style={{ maxWidth:480, margin:'0 auto', position:'relative' }}>
      {/* Left gold edge glow */}
      <div style={{ position:'absolute', top:0, left:0, bottom:0, width:5, zIndex:10,
        pointerEvents:'none', borderRadius:'20px 0 0 20px',
        background:'linear-gradient(180deg, transparent 5%, rgba(255,248,215,0.72) 28%, rgba(255,255,232,0.88) 50%, rgba(255,248,215,0.72) 72%, transparent 95%)',
        boxShadow:'4px 0 14px rgba(255,240,180,0.22)' }} />
      {/* Right lavender edge glow */}
      <div style={{ position:'absolute', top:0, right:0, bottom:0, width:5, zIndex:10,
        pointerEvents:'none', borderRadius:'0 20px 20px 0',
        background:'linear-gradient(180deg, transparent 5%, rgba(208,192,244,0.46) 28%, rgba(226,214,254,0.76) 50%, rgba(208,192,244,0.46) 72%, transparent 95%)',
        boxShadow:'-4px 0 14px rgba(198,182,254,0.18)' }} />

      {/* Card — NO overflow:hidden so backdrop-filter children work */}
      <div style={{
        borderRadius:20, position:'relative',
        background:[
          'linear-gradient(135deg, transparent 8%, rgba(198,167,90,0.015) 30%, transparent 56%)',
          'linear-gradient(160deg, rgba(255,255,253,0.97) 0%, rgba(252,248,240,0.95) 55%, rgba(248,243,232,0.94) 100%)',
        ].join(', '),
        border:'1.5px solid rgba(255,253,244,0.85)',
        // Glass material upgrade: stronger rim, internal diffusion, deep shadow
        boxShadow:[
          'inset 0 2.5px 0 rgba(255,255,255,0.98)',        // bright top bevel
          'inset 0 4px 36px rgba(255,248,220,0.52)',        // warm internal light diffusion
          'inset 2px 0 10px rgba(255,252,236,0.22)',        // left inner edge catch
          'inset -2px 0 10px rgba(255,252,236,0.22)',       // right inner edge catch
          'inset 0 -1px 0 rgba(198,167,90,0.16)',           // bottom gold tint
          '0 2px 4px rgba(110,88,30,0.06)',
          '0 16px 52px rgba(110,88,30,0.14)',
          '0 42px 90px rgba(110,88,30,0.10)',
          '0 0 0 1.5px rgba(198,167,90,0.30)',              // gold rim
          '0 0 36px rgba(198,167,90,0.08)',                 // outer warm halo
        ].join(', '),
      }}>
        {/* Inner decorative clip zone — isolated overflow:hidden for botanical + bevel edges */}
        <div style={{ position:'absolute', inset:0, borderRadius:20,
          overflow:'hidden', pointerEvents:'none', zIndex:1 }}>
          <Botanical />
          {/* Top bevel */}
          <div style={{ position:'absolute', top:0, left:0, right:0, height:2,
            background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.96) 25%, white 50%, rgba(255,255,255,0.96) 75%, transparent)' }} />
          {/* Inner top light diffusion */}
          <div style={{ position:'absolute', top:0, left:'5%', right:'5%', height:'40%',
            borderRadius:'50%',
            background:'radial-gradient(ellipse, rgba(255,255,255,0.58) 0%, rgba(255,251,235,0.22) 40%, transparent 72%)',
            filter:'blur(22px)' }} />
          {/* Bottom gold line */}
          <div style={{ position:'absolute', bottom:0, left:0, right:0, height:1,
            background:'linear-gradient(90deg, transparent 5%, rgba(198,167,90,0.28) 30%, rgba(198,167,90,0.28) 70%, transparent 95%)' }} />
        </div>

        {/* Content */}
        <div style={{ position:'relative', zIndex:2, padding:'32px 28px 28px' }}>

          {/* Welcome header */}
          <div style={{ textAlign:'center', marginBottom:22 }}>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:28, fontWeight:700,
              margin:'0 0 6px', letterSpacing:'2px', ...goldEtched }}>
              Welcome
            </p>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:18, fontWeight:600,
              color:'#1A1410', margin:'0 0 7px', letterSpacing:'0.5px',
              textShadow:'0 1px 0 rgba(255,255,255,0.85)' }}>
              {MOCK_PARTY.party_name}
            </p>
            <p style={{ fontSize:11, color:'#8A7A5C', margin:0,
              letterSpacing:'0.3px', fontStyle:'italic' }}>
              We have been expecting you
            </p>
          </div>

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg, transparent, rgba(198,167,90,0.28))' }} />
            <span style={{ color:'rgba(198,167,90,0.50)', fontSize:10 }}>✦</span>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg, rgba(198,167,90,0.28), transparent)' }} />
          </div>

          {/* Accept / Decline */}
          {!isSubmitted && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
              <button style={{
                padding:'13px 8px', borderRadius:12, border:'none', cursor:'pointer', outline:'none',
                background: isAccepting
                  ? 'linear-gradient(145deg, rgba(30,110,65,0.15), rgba(30,110,65,0.09))'
                  : 'rgba(238,248,242,0.72)',
                boxShadow: isAccepting
                  ? '0 0 18px rgba(30,130,75,0.18), inset 0 1px 0 rgba(255,255,255,0.65), 0 0 0 1.5px rgba(30,130,75,0.36)'
                  : '0 0 10px rgba(30,130,75,0.08), inset 0 1px 0 rgba(255,255,255,0.65), 0 0 0 1px rgba(30,130,75,0.20)',
                transition:'all 0.18s ease',
              }}>
                <span style={{ fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:600,
                  letterSpacing:'0.4px', color: isAccepting ? '#18603A' : '#2A7A4E' }}>
                  Joyfully Accept
                </span>
              </button>
              <button style={{
                padding:'13px 8px', borderRadius:12, border:'none', cursor:'pointer', outline:'none',
                background: isDeclining
                  ? 'linear-gradient(145deg, rgba(150,55,55,0.13), rgba(150,55,55,0.07))'
                  : 'rgba(252,244,244,0.72)',
                boxShadow: isDeclining
                  ? '0 0 18px rgba(150,60,60,0.16), inset 0 1px 0 rgba(255,255,255,0.60), 0 0 0 1.5px rgba(150,60,60,0.33)'
                  : '0 0 10px rgba(150,60,60,0.07), inset 0 1px 0 rgba(255,255,255,0.65), 0 0 0 1px rgba(150,60,60,0.16)',
                transition:'all 0.18s ease',
              }}>
                <span style={{ fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:600,
                  letterSpacing:'0.4px', color: isDeclining ? '#8A2828' : '#9B3838' }}>
                  Regretfully No
                </span>
              </button>
            </div>
          )}

          {/* PENDING */}
          {isPending && (
            <p style={{ fontSize:12, color:'#9B8A68', textAlign:'center',
              fontStyle:'italic', padding:'8px 0 4px', margin:0 }}>
              Please make your selection above
            </p>
          )}

          {/* ACCEPTING: guest rows → Menu → Submit */}
          {isAccepting && (
            <div>
              {MOCK_PARTY.guests.map((g) => (
                <GuestRow
                  key={g.id}
                  guest={g}
                  index={MOCK_PARTY.guests.indexOf(g)}
                  guestType={guestTypes[g.id] || null}
                  onGuestTypeChange={(type) => handleGuestTypeChange(g.id, type)}
                  showValidation={showValidation}
                />
              ))}

              {/* Menu — after guests, before Submit */}
              <div style={{ margin:'18px 0 18px' }}>
                <p style={{ fontSize:9, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:'1.5px', color:'rgba(198,167,90,0.65)',
                  marginBottom:12, textAlign:'center' }}>
                  Menu
                </p>
                <MenuTiles activeMenu={activeMenu} setActiveMenu={setActiveMenu} menuData={MOCK_MENU} />
              </div>

              <button onClick={handleSubmit} style={{ width:'100%', height:50, borderRadius:12, marginTop:4,
                background:'linear-gradient(145deg, #D4AA50 0%, #C49830 35%, #B48820 70%, #C49830 100%)',
                border:'1px solid rgba(198,160,40,0.50)',
                boxShadow:'0 4px 14px rgba(180,130,20,0.34), inset 0 1.5px 0 rgba(255,235,130,0.40)',
                cursor:'pointer', fontSize:12, fontWeight:700, color:'#FFF5D0',
                letterSpacing:'1.2px', textTransform:'uppercase',
                textShadow:'0 1px 3px rgba(100,60,0,0.25)', outline:'none' }}>
                Submit RSVP
              </button>
            </div>
          )}

          {/* DECLINING */}
          {isDeclining && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <p style={{ fontSize:13, color:'#5A4E38', lineHeight:1.75, textAlign:'center',
                margin:0, fontStyle:'italic' }}>
                We're sorry you're unable to join us.<br/>
                If your plans change, please don't hesitate to reach out —<br/>
                we would truly love to have you with us.
              </p>
              <textarea placeholder="Leave a message (optional)" rows={2}
                style={{ padding:'10px 12px', borderRadius:10, resize:'none',
                  border:'1.5px solid rgba(198,167,90,0.26)',
                  background:'rgba(255,253,246,0.72)',
                  boxShadow:'inset 0 1px 0 rgba(255,255,255,0.65)',
                  fontSize:12, color:'#2A2218', fontFamily:'inherit', outline:'none' }} />
              <button style={{ width:'100%', height:50, borderRadius:12,
                background:'linear-gradient(145deg, #D4AA50 0%, #C49830 35%, #B48820 70%, #C49830 100%)',
                border:'1px solid rgba(198,160,40,0.50)',
                boxShadow:'0 4px 14px rgba(180,130,20,0.34), inset 0 1.5px 0 rgba(255,235,130,0.40)',
                cursor:'pointer', fontSize:12, fontWeight:700, color:'#FFF5D0',
                letterSpacing:'1.2px', textTransform:'uppercase',
                textShadow:'0 1px 3px rgba(100,60,0,0.25)', outline:'none' }}>
                Submit RSVP
              </button>
            </div>
          )}

          {/* SUBMITTED */}
          {isSubmitted && (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
              gap:12, padding:'8px 0' }}>
              <div style={{ width:50, height:50, borderRadius:'50%',
                background:'linear-gradient(145deg, rgba(235,255,242,0.96), rgba(200,240,218,0.86))',
                border:'1.5px solid rgba(34,160,90,0.28)',
                boxShadow:'0 2px 14px rgba(34,160,90,0.16), inset 0 1px 0 rgba(255,255,255,0.80)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:22, color:'#2D7A4F' }}>✓</div>
              <p style={{ fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:700,
                color:'#1E1A14', margin:0, textAlign:'center' }}>RSVP Submitted</p>
              <p style={{ fontSize:11, color:'#7A6A50', textAlign:'center',
                lineHeight:1.7, margin:0 }}>
                Thank you, {MOCK_PARTY.guests[0]?.first_name}.<br/>
                We look forward to celebrating with you.
              </p>
              <button style={{ marginTop:4, padding:'10px 22px',
                background:'rgba(30,26,20,0.06)',
                border:'1.5px solid rgba(198,167,90,0.28)', borderRadius:10,
                cursor:'pointer', fontSize:11, fontWeight:600, color:'#5A4E38',
                boxShadow:'inset 0 1px 0 rgba(255,255,255,0.60)', outline:'none' }}>
                Verify &amp; Modify Attendance
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sandbox Page ─────────────────────────────────────────────────────────────
export default function DesignLab() {
  const [rsvpState, setRsvpState] = useState('pending');

  return (
    <div style={{ minHeight:'100vh', background:'#F5EFE6',
      fontFamily:"'Inter',-apple-system,sans-serif" }}>

      <div style={{ background:'#1B1812', padding:'14px 28px', display:'flex',
        alignItems:'center', justifyContent:'space-between',
        borderBottom:'2px solid rgba(198,167,90,0.40)', position:'sticky', top:0, zIndex:200 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:7, height:7, borderRadius:'50%', background:'#C6A75A',
            boxShadow:'0 0 7px rgba(198,167,90,0.55)' }}/>
          <span style={{ color:'rgba(198,167,90,0.85)', fontSize:11, fontWeight:700,
            textTransform:'uppercase', letterSpacing:'1.5px' }}>Design Lab</span>
          <span style={{ color:'rgba(255,255,255,0.25)', fontSize:11 }}>·</span>
          <span style={{ color:'rgba(255,255,255,0.38)', fontSize:11 }}>Phase 0 · Sandbox only · No live data</span>
        </div>
      </div>

      <div style={{ maxWidth:540, margin:'0 auto', padding:'36px 24px 60px' }}>
        <p style={{ fontSize:10, fontWeight:700, textTransform:'uppercase',
          letterSpacing:'1.2px', color:'#9B8D6E', marginBottom:10 }}>RSVP Module</p>

        <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginBottom:24 }}>
          {RSVP_STATES.map(s => (
            <button key={s.key} onClick={() => setRsvpState(s.key)} style={{
              padding:'8px 14px', borderRadius:9, fontSize:12, fontWeight:600,
              cursor:'pointer', outline:'none',
              border: rsvpState===s.key ? `2px solid ${s.color}` : '2px solid rgba(155,141,110,0.22)',
              background: rsvpState===s.key ? `${s.color}12` : 'rgba(255,255,255,0.50)',
              color: rsvpState===s.key ? s.color : '#7A6A40',
              transition:'all 0.14s ease',
            }}>{s.label}</button>
          ))}
        </div>

        <RSVPCard rsvpState={rsvpState} />

        <div style={{ marginTop:40, paddingTop:16,
          borderTop:'1px solid rgba(155,141,110,0.15)',
          display:'flex', justifyContent:'space-between' }}>
          <p style={{ fontSize:11, color:'#A89878' }}>Mock data only · Stark Family</p>
          <p style={{ fontSize:11, color:'#A89878' }}>Phase 0 · Not connected to production</p>
        </div>
      </div>
    </div>
  );
}
